import type { CancelTokenSource } from 'axios'
import _ from 'lodash'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import { computed, makeObservable, observable, toJS } from 'mobx'
import { format } from 'sql-formatter'

import { MAX_PROJECTIONS_BY_CHART_TYPE } from 'modules/reports/lib/Constants'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import fieldAttributes from 'modules/reports/store/collections/fields'
import { ReportShares } from 'modules/reports/store/collections/reports_shares'
import Content, { CreateParams } from 'modules/reports/store/models/Content'
import type { FilterParams } from 'modules/reports/store/models/Filter'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'
import Report, {
  interpolateVariablesInQuery,
  variableValueToBackend,
} from 'modules/reports/store/models/Report'
import type { SqlConfiguration, Variable } from 'modules/reports/store/models/Report'
import type { ReportShare } from 'modules/reports/store/models/ReportShare'
import type { ResourceData } from 'modules/reports/store/models/Resource'
import Template from 'modules/reports/store/models/Template'

import canRender from './canRender'

type Resource = Report | Template
interface StructProps {
  resource: Resource
  projections: ProjectionParams[]
  filters: FilterParams[]
  shares: ReportShare[]
  sqlQuery: string | null | undefined
  sqlConfiguration?: SqlConfiguration | null | undefined
  resourceData?: ResourceData | null | undefined
}

export interface SerializedReportStruct {
  id: string | null | undefined
  projections: ProjectionParams[]
  filters: FilterParams[]
  sql_query: string | null | undefined
  sql_configuration: SqlConfiguration | null | undefined
  variables: Variable[]
}

// This method replaces any variable argument to its variable value if it's
// defined
const replaceFilterArguments = (filters: FilterParams[], variables: Variable[]) => {
  return filters.map((filterGroup) => {
    return filterGroup.map((filter) => {
      const filterArgs = filter.arguments.map((argument) => {
        variables.forEach((variable) => {
          const value = variable.value || variable.default_value

          if (variable.name === argument.value && argument.use_variable && value) {
            argument.value = variableValueToBackend(variable.type, value)
          }
        })

        return argument
      })

      return { ...filter, arguments: filterArgs }
    })
  })
}

class ReportStruct<T extends Resource = Resource> {
  props: StructProps & { resource: T }

  constructor(props: StructProps & { resource: T }) {
    this.props = props
    makeObservable(this, {
      props: observable,
      reportId: computed,
      templateSlug: computed,
      title: computed,
      identifier: computed,
      type: computed,
      maxProjections: computed,
      chartSelected: computed,
      firstAggregation: computed,
      canRender: computed,
      jsonQuery: observable,
      isSQL: computed,
      isBuilder: computed,
    })
  }

  static buildFromReport(report: Report): ReportStruct<Report> {
    const projections = report.projections.map((m) => m.serialize())

    const filters = report.filters.map((d) => d.serialize())
    const sqlQuery = report.sqlQuery
    const sqlConfiguration = report.sqlConfiguration
    const shares = report.id ? ReportShares.ofReport(report.id) : []

    return new ReportStruct({
      resource: report,
      projections,
      filters,
      shares,
      sqlQuery,
      sqlConfiguration,
    })
  }

  static buildFromTemplate(template: Template): ReportStruct<Template> {
    const projections = template.projections.map((m) => m.serialize())

    const filters = template.filters.map((d) => d.serialize())
    const { sqlQuery, sqlConfiguration } = template

    return new ReportStruct({
      resource: template,
      projections,
      filters,
      shares: [],
      sqlQuery,
      sqlConfiguration,
    })
  }

  async generate(variables: Variable[], cancelToken: CancelTokenSource | null = null) {
    const { resource } = this.props
    let templateId = null
    if (resource instanceof Template) {
      templateId = resource.id
    }
    let reportId = null
    if (resource instanceof Report) {
      reportId = resource.id
    }
    const withoutId = false
    const content = new Content() // FIXME: Currently we lost the content object.
    const resourceData = await content.create(
      this.serializeToCreate(variables, withoutId, reportId, templateId),
      cancelToken
    )
    this.props.resourceData = resourceData
    return resourceData
  }

  order(variables: Variable[]) {
    const { resource } = this.props

    return resource.order(this.serialize(variables))
  }

  async toSql(variables: Variable[]): Promise<any> {
    const { resource } = this.props
    const result = await (resource as Report).toSql(this.serialize(variables))
    const regex = new RegExp(/`/, 'g')

    return format(result.sql.replace(regex, ''), {
      language: 'sqlite',
    })
  }

  /**
   * Serialize to Create.
   * @param variables
   * @param withoutId
   * @param reportId
   * @param templateId
   * @returns {CreateParams}
   */
  serializeToCreate(
    variables: Variable[] = [],
    withoutId = false,
    reportId = null,
    templateId = null
  ): CreateParams {
    return {
      report_id: reportId,
      template_id: templateId,
      fallback: true,
      sql_configuration: JSON.stringify(toJS(this.sqlConfiguration)),
      sql_query: toJS(this.renderedSqlQuery(this.sqlQuery, variables)),
      json_query: this.jsonQuery(variables, withoutId),
    }
  }

  serialize(variables: Variable[] = [], withoutId = false): SerializedReportStruct {
    const { projections, filters, sqlQuery, sqlConfiguration } = this.props
    let serializedProjections = toJS(projections).slice(0, this.maxProjections)
    let serializedFilters = toJS(filters)

    if (withoutId) {
      const removeId = <Type extends object>(theObject: Type) =>
        omitBy(theObject, (_: unknown, k: string) => k === 'id') as {
          [Key in Exclude<keyof Type, 'id'>]: Type[Key]
        }

      serializedFilters = serializedFilters.map((f) => f.map(removeId))
      serializedProjections = serializedProjections.map(removeId)
    }

    // Replacing values for its filters ones
    const renderedFilters = replaceFilterArguments(serializedFilters, variables)

    return {
      id: this.reportId,
      projections: serializedProjections,
      filters: renderedFilters,
      sql_query: this.renderedSqlQuery(toJS(sqlQuery), variables),
      sql_configuration: toJS(sqlConfiguration),
      variables: variables,
    }
  }

  get reportId() {
    return this.props.resource.id
  }

  get templateSlug(): string {
    return (this.props.resource as Template).slug
  }

  get title(): string {
    return this.props.resource.title
  }

  get identifier(): string {
    return (this.props.resource as Report).identifier
  }

  get type(): string {
    return this.props.resource.type
  }

  get projections(): ProjectionParams[] {
    return this.props.projections
  }

  get filters(): FilterParams[] {
    return this.props.filters
  }

  get sqlQuery(): string | null | undefined {
    return this.props.sqlQuery
  }

  get sqlConfiguration(): SqlConfiguration | null | undefined {
    return this.props.sqlConfiguration
  }

  /**
   * JSON Query.
   *
   * A SQL Query turn into JSON coming from template or builder tools.
   *
   * @param variables
   * @param withoutId
   * @returns {string} Serialized in the format that the backend /api/rest/content accepts.
   */
  jsonQuery(variables: Variable[] = [], withoutId = false): string | null | undefined {
    let serializedProjections = toJS(this.projections).slice(0, this.maxProjections)
    let serializedFilters = toJS(this.filters)
    if (withoutId) {
      const removeId = <Type extends object>(theObject: Type) =>
        omitBy(theObject, (_: unknown, k: string) => k === 'id') as {
          [Key in Exclude<keyof Type, 'id'>]: Type[Key]
        }

      serializedFilters = serializedFilters.map((f) => f.map(removeId))
      serializedProjections = serializedProjections.map(removeId)
    }
    const renderedFilters = replaceFilterArguments(serializedFilters, variables)
    const apiFmtFilters = renderedFilters.map((items) => {
      return {
        conditions: items,
      }
    })
    const emptyFilters = apiFmtFilters == null || apiFmtFilters.length == 0
    const emptyProjections = serializedProjections == null || serializedProjections.length == 0
    if (emptyFilters && emptyProjections) {
      return ''
    }

    apiFmtFilters.forEach((filter) => {
      filter.conditions.forEach((condition) => {
        const attribute = fieldAttributes.get(condition.path)?.attributes
        const fieldType = attribute?.type
        const fieldPath = attribute?.path
        const isCustomFieldPath = fieldPath?.includes('custom_field') ?? false

        if (fieldType == 'number' && isCustomFieldPath) {
          condition.arguments = condition.arguments.map((argument) => {
            const valueToCents = Number(argument.value) * 100

            return {
              ...argument,
              value: valueToCents.toString(),
            }
          })
        }
      })
    })

    const jsonQuery = JSON.stringify({
      filters: apiFmtFilters,
      projections: serializedProjections,
    })
    return jsonQuery
  }

  private renderedSqlQuery(query: string | null | undefined, variables: Variable[]) {
    if (isNil(query)) return undefined

    return interpolateVariablesInQuery(query, variables)
  }

  get maxProjections(): number {
    return MAX_PROJECTIONS_BY_CHART_TYPE[this.type] ?? 0
  }

  get chartSelected(): boolean {
    return this.props.resource.chartSelected
  }

  get firstAggregation(): ProjectionParams | undefined {
    return this.props.projections.find((p) => {
      return !_.isEmpty(p.function)
    })
  }

  get canRender(): boolean {
    if (this.isSQL) return true
    return canRender(this.props)
  }

  get isPie(): boolean {
    return this.type === Report.CHART_TYPES.pie
  }

  get isScatterplot(): boolean {
    return this.type === Report.CHART_TYPES.scatterplot
  }

  get isSQL(): boolean {
    return this.props.sqlQuery != null && this.props.sqlQuery.length > 0
  }

  get projectionsAvailable(): boolean {
    return this.props.projections.length > 0
  }

  get isBuilder(): boolean {
    return this.projectionsAvailable && this.firstAggregation != null
  }

  get hasFiltersWithVariables(): boolean {
    return this.props.filters.some((filterGroup) => {
      return filterGroup.some((filter) => {
        return filter.arguments.some((argument) => {
          return argument.use_variable
        })
      })
    })
  }
}

export default ReportStruct
